import React, { useMemo, useCallback } from "react";
import RcSlider from "rc-slider";
import "rc-slider/assets/index.css";
import { Tooltip } from "antd";
import _ from "lodash";

function groupByDistance(items, threshold = 3) {
    const groups = [];
    let current = [];

    const sorted = [...items].sort((a, b) => parseFloat(a.index) - parseFloat(b.index));

    sorted.forEach((item) => {
        const idx = parseFloat(item.index);

        if (current.length === 0) {
            current.push(item);
            return;
        }

        const lastIdx = parseFloat(current[current.length - 1].index);
        const firstIdx = parseFloat(current[0].index);

        const shouldGroup = Math.abs(idx - lastIdx) <= threshold && Math.abs(idx - firstIdx) <= threshold;

        if (shouldGroup) {
            current.push(item);
        } else {
            groups.push(current);
            current = [item];
        }
    });

    if (current.length) groups.push(current);
    return groups;
}

export default function CustomSlider(props) {
    const { marks = {}, value, min, max, onChange } = props;

    const calculatedThreshold = useMemo(() => (max - min) / 30, [min, max]);

    const handleMarkClick = useCallback(
        (index) => {
            if (typeof onChange === "function") {
                onChange(index);
            }
        },
        [onChange],
    );

    const { topMarks, bottomMarks } = useMemo(() => {
        const top = [];
        const bottom = [];

        Object.keys(marks).forEach((key) => {
            const idx = key;
            const items = Array.isArray(marks[key]) ? marks[key] : [marks[key]];

            items.forEach((item) => {
                const mark = {
                    index: idx,
                    label: item.label,
                    left: ((parseFloat(idx) - min) / (max - min)) * 100 + "%",
                    tooltip: item.tooltip || null,
                    style: item.style ?? {},
                    position: item.position || "bottom",
                    type: item.type || "default",
                };

                if (_.includes(item.position, "top")) {
                    top.push(mark);
                } else {
                    bottom.push(mark);
                }
            });
        });

        return { topMarks: top, bottomMarks: bottom };
    }, [marks, min, max]);

    const groupedTop = useMemo(() => groupByDistance(topMarks, calculatedThreshold), [topMarks, calculatedThreshold]);
    const hasAnyTopLower = useMemo(() => topMarks.some((m) => m.position === "top_lower"), [topMarks]);

    return (
        <div
            className={`${props.className}`}
            style={{ position: "relative", paddingTop: 40, paddingBottom: 40 }}>
            {/* TOP LABELS */}
            <div className={`custom-slider-marks top`}>
                {groupedTop.map((group, i) => {
                    const first = group[0];
                    const isGroup = group.length === 1 ? false : true;

                    const tooltipContent = (
                        <div className="custom-slider-ole-patrols-tooltip-list">
                            {group.map((g, idx) => (
                                <div
                                    onClick={() => handleMarkClick(g.index)}
                                    key={idx}>
                                    {g.tooltip}
                                </div>
                            ))}
                        </div>
                    );

                    return (
                        <Tooltip
                            title={!isGroup && first.type === "elr_marker" ? null : tooltipContent}
                            overlayClassName="custom-slider-ole-patrols-tooltip"
                            className={`${isGroup ? "custom-slider-ole-patrols-tooltip-group" : ""}`}
                            placement={isGroup ? "topLeft" : "top"}
                            arrowPointAtCenter
                            key={i}>
                            <div
                                className={`custom-slider-mark custom-slider-mark-${hasAnyTopLower ? first.position : "top_lower"} ${first.type} ${_.floor(first.index, 1) === _.floor(value, 1) ? "selected" : ""}`}
                                style={{ left: first.left }}
                                onClick={() => handleMarkClick(first.index)}>
                                {first.label}
                            </div>
                        </Tooltip>
                    );
                })}
            </div>

            {/* SLIDER */}
            <RcSlider
                {...props}
                onChange={onChange}
            />

            {/* BOTTOM LABELS */}
            <div className="custom-slider-marks bottom">
                {bottomMarks.map((m, i) => (
                    <Tooltip
                        title={m.tooltip}
                        key={`tooltip-bottom-${m.index}-${i}`}>
                        <div
                            key={`b-${m.index}-${i}`}
                            className={`custom-slider-mark custom-slider-mark-bottom ${_.floor(m.index, 1) === _.floor(value, 1) ? "selected" : ""}`}
                            style={{ left: m.left, ...m.style }}
                            onClick={() => handleMarkClick(m.index)}>
                            {m.label}
                        </div>
                    </Tooltip>
                ))}
            </div>
        </div>
    );
}
